/**
 * The config cascade's one layering rule, for every reader and writer of a layered document.
 *
 * A config is layers: the code defaults, the workspace's document, an integration's, a member's. Reading lays each
 * over the one beneath ({@link resolveConfigLayers}); writing lays a patch over the stored document
 * ({@link layConfigPatch}). Both follow the rule written once here, so the platform's read and write paths, the web's
 * local mirror, and internal ops' override diff can never disagree about what a `null`, an array, or a whole-value key
 * means. The rule is deliberately dumb: objects layer key by key, arrays and scalars replace, `null` inherits. A
 * document that needs a value to replace whole spells it as an array or a string; {@link ReplaceWhole} carries the one
 * legacy exception. No dependencies, browser-safe.
 */

/**
 * The keys whose object value replaces the object beneath whole instead of layering into it key by key.
 *
 * Bare key names, not paths. The rule of the cascade is that objects layer, arrays and scalars replace, and `null`
 * inherits; this set is the one exception, kept for values that were records before the rule was settled. A new
 * config value that must replace whole is an array or a string, never a record added here: a record cannot be pruned
 * under layering, since `null` inherits, and a set of exceptions is how the cascade stopped being one rule before.
 */
export type ReplaceWhole = ReadonlySet<string>;

/**
 * No exceptions: every object layers key by key.
 */
export const REPLACE_NOTHING: ReplaceWhole = new Set();

/**
 * What a `null` in a layer means.
 *
 * On the read path it inherits the value beneath; on the write path it is stored as the reset it is, for the read
 * path to resolve later.
 */
export type NullRule = "inherit" | "store";

/**
 * Whether a value is a plain object: the only kind the cascade layers into.
 */
export function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Lays one value over the one beneath.
 *
 * `undefined` leaves the key alone. `null` inherits the value beneath, or `null` when nothing lies there, on the read
 * path, and is stored as `null` on the write path. An array replaces whole. An object replaces whole under a key in
 * {@link ReplaceWhole}, or when what lies beneath is not an object; otherwise it layers into the object beneath key
 * by key. Anything else replaces.
 */
function lay(beneath: unknown, value: unknown, replaceWhole: ReplaceWhole, nulls: NullRule, whole: boolean): unknown {
  if (value === null) {
    return nulls === "inherit" ? (beneath ?? null) : null;
  }
  if (whole || !isPlainObject(value)) {
    return value;
  }
  return layInto(isPlainObject(beneath) ? beneath : {}, value, replaceWhole, nulls);
}

function layInto(
  beneath: Record<string, unknown>,
  layer: Record<string, unknown>,
  replaceWhole: ReplaceWhole,
  nulls: NullRule,
): Record<string, unknown> {
  const laid: Record<string, unknown> = { ...beneath };
  for (const [key, value] of Object.entries(layer)) {
    if (value === undefined) {
      continue;
    }
    laid[key] = lay(beneath[key], value, replaceWhole, nulls, replaceWhole.has(key));
  }
  return laid;
}

/**
 * The config a stack of layers resolves to: each laid over the one before, `null` inheriting.
 *
 * The first layer is the bottom, normally the code defaults. A layer that is not an object lays nothing. Objects are
 * copied as they are laid, so no layer is mutated and the result shares nothing with the layers except the arrays
 * and whole values it took by reference.
 *
 * @param replaceWhole - The keys whose object value replaces whole; {@link REPLACE_NOTHING} for none.
 * @param layers - Bottom first.
 */
export function resolveConfigLayers(
  replaceWhole: ReplaceWhole,
  ...layers: readonly unknown[]
): Record<string, unknown> {
  let resolved: Record<string, unknown> = {};
  for (const layer of layers) {
    if (isPlainObject(layer)) {
      resolved = layInto(resolved, layer, replaceWhole, "inherit");
    }
  }
  return resolved;
}

/**
 * A stored document with a patch laid over it, `null` stored as the reset it is.
 *
 * What the write path stores and what a later patch lays over, so a client mirroring its own PATCH gets the document
 * the server will hold. Nothing is validated here: the wire schema is the validation.
 *
 * @param replaceWhole - The keys whose object value replaces whole; {@link REPLACE_NOTHING} for none.
 * @param stored - The document as stored, or none.
 * @param patch - The keys that changed.
 */
export function layConfigPatch(
  replaceWhole: ReplaceWhole,
  stored: Record<string, unknown> | null | undefined,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  return layInto(stored ?? {}, patch, replaceWhole, "store");
}

/**
 * A value frozen to every depth, so a code default the cascade hands out by reference can never be changed in place.
 */
export function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    for (const child of Object.values(value as Record<string, unknown>)) {
      deepFreeze(child);
    }
    Object.freeze(value);
  }
  return value;
}
